import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Loader2,
  Minus,
  Plus,
  RefreshCw,
  Trophy,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  getGameRpsConfig,
  getGameRpsRanking,
  getGameRpsSeasons,
  patchGameRpsConfig,
} from '../../api/gameRps';
import { t } from '../../utils/i18n';
import { useUsersByIds } from '../../hooks/useUsersByIds';
import UserChip from '../../components/UserChip';
import GameRpsTabs from './GameRpsTabs';

const DEFAULT_MIN_WINS = 5;
const DEFAULT_GOLD = 5;
const DEFAULT_SILVER = 20;
const DEFAULT_BRONZE = 75;
const DEFAULT_TOP_N = 100;
const MIN_WINS_FLOOR = 1;
const MIN_WINS_CEIL = 1000;
const TOP_N_FLOOR = 1;
const TOP_N_CEIL = 1000;
const PERIODS = ['weekly', 'daily', 'monthly'];

function resolveNum(payload, ...keys) {
  const data = payload?.data ?? payload ?? {};
  for (const k of keys) {
    if (data[k] != null && data[k] !== '') {
      const n = Number(data[k]);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

function clampWins(n) {
  if (!Number.isFinite(n)) return DEFAULT_MIN_WINS;
  return Math.min(MIN_WINS_CEIL, Math.max(MIN_WINS_FLOOR, Math.trunc(n)));
}

function clampTopN(n) {
  if (!Number.isFinite(n)) return DEFAULT_TOP_N;
  return Math.min(TOP_N_CEIL, Math.max(TOP_N_FLOOR, Math.trunc(n)));
}

function badgeCutoffs(topN, goldPct, silverPct) {
  const n = topN > 0 ? topN : DEFAULT_TOP_N;
  const goldEnd = Math.floor((n * goldPct) / 100);
  const silverEnd = Math.min(n, goldEnd + Math.floor((n * silverPct) / 100));
  return { goldEnd, silverEnd };
}

function RankBadge({ rank }) {
  if (rank === 1) {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-black text-xs font-bold shadow-[0_0_0_3px_rgba(255,192,0,0.18)]">
        1
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-zinc-300 text-zinc-900 text-xs font-bold">
        2
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-700 text-amber-50 text-xs font-bold">
        3
      </span>
    );
  }
  return (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-dark-surface text-dark-text-secondary text-xs font-semibold tabular-nums">
      {rank}
    </span>
  );
}

function TierChip({ tier }) {
  if (tier == null) return null;
  const map = {
    3: { label: t('gameRps.tier.gold'), cls: 'border-primary/40 bg-primary/15 text-primary' },
    2: { label: t('gameRps.tier.silver'), cls: 'border-zinc-400/40 bg-zinc-400/15 text-zinc-200' },
    1: { label: t('gameRps.tier.bronze'), cls: 'border-amber-700/40 bg-amber-800/20 text-amber-200' },
  };
  const m = map[tier];
  if (!m) return null;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${m.cls}`}>
      {m.label}
    </span>
  );
}

function PctField({ label, value, onChange, accent }) {
  return (
    <label className="flex flex-col gap-1.5 rounded-lg border border-dark-border bg-dark-bg/50 p-3">
      <span className={`text-[11px] font-semibold uppercase tracking-wide ${accent}`}>{label}</span>
      <div className="flex items-center gap-1">
        <input
          type="number"
          min={1}
          max={98}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-dark-border bg-dark-card text-dark-text px-2.5 py-2 text-lg font-bold tabular-nums focus:border-primary/60 focus:ring-1 focus:ring-primary/35 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <span className="text-dark-text-muted text-sm">%</span>
      </div>
    </label>
  );
}

export default function GameRps() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'ranking' ? 'ranking' : 'config';
  const [minWins, setMinWins] = useState(String(DEFAULT_MIN_WINS));
  const [topN, setTopN] = useState(String(DEFAULT_TOP_N));
  const [goldPct, setGoldPct] = useState(String(DEFAULT_GOLD));
  const [silverPct, setSilverPct] = useState(String(DEFAULT_SILVER));
  const [bronzePct, setBronzePct] = useState(String(DEFAULT_BRONZE));
  const [saved, setSaved] = useState({
    minWins: DEFAULT_MIN_WINS,
    topN: DEFAULT_TOP_N,
    gold: DEFAULT_GOLD,
    silver: DEFAULT_SILVER,
    bronze: DEFAULT_BRONZE,
  });
  const [formError, setFormError] = useState('');
  const [saveFlash, setSaveFlash] = useState(false);
  const [rankMode, setRankMode] = useState('season'); // season | period
  const [period, setPeriod] = useState('weekly');
  const [seasonKey, setSeasonKey] = useState('');

  const {
    data: configData,
    isLoading: configLoading,
    isError: configError,
    error: configErr,
    refetch: refetchConfig,
    isFetching: configFetching,
  } = useQuery({
    queryKey: ['game-rps-config'],
    queryFn: () => getGameRpsConfig(token),
    enabled: !!token,
  });

  const {
    data: seasonsData,
    refetch: refetchSeasons,
    isFetching: seasonsFetching,
  } = useQuery({
    queryKey: ['game-rps-seasons'],
    queryFn: () => getGameRpsSeasons(token),
    enabled: !!token && tab === 'ranking',
  });

  const seasons = seasonsData?.data?.seasons || [];
  const currentSeasonKey = seasonsData?.data?.current_key || '';

  React.useEffect(() => {
    if (!seasonKey && currentSeasonKey) setSeasonKey(currentSeasonKey);
  }, [currentSeasonKey, seasonKey]);

  const rankingQueryKey =
    rankMode === 'season'
      ? ['game-rps-ranking', 'season', seasonKey]
      : ['game-rps-ranking', 'period', period];

  const {
    data: rankingData,
    isLoading: rankingLoading,
    isError: rankingError,
    error: rankingErr,
    refetch: refetchRanking,
    isFetching: rankingFetching,
  } = useQuery({
    queryKey: rankingQueryKey,
    queryFn: () =>
      rankMode === 'season'
        ? getGameRpsRanking(token, { seasonKey })
        : getGameRpsRanking(token, { period }),
    enabled:
      !!token &&
      tab === 'ranking' &&
      (rankMode === 'period' || !!seasonKey),
  });

  React.useEffect(() => {
    if (!configData) return;
    const n = resolveNum(configData, 'min_wins_for_ranking') ?? DEFAULT_MIN_WINS;
    const top = resolveNum(configData, 'ranking_top_n') ?? DEFAULT_TOP_N;
    const g = resolveNum(configData, 'badge_gold_pct') ?? DEFAULT_GOLD;
    const s = resolveNum(configData, 'badge_silver_pct') ?? DEFAULT_SILVER;
    const b = resolveNum(configData, 'badge_bronze_pct') ?? DEFAULT_BRONZE;
    setMinWins(String(n));
    setTopN(String(top));
    setGoldPct(String(g));
    setSilverPct(String(s));
    setBronzePct(String(b));
    setSaved({ minWins: n, topN: top, gold: g, silver: s, bronze: b });
  }, [configData]);

  const mutation = useMutation({
    mutationFn: (body) => patchGameRpsConfig(token, body),
    onSuccess: (_data, body) => {
      setFormError('');
      setSaved({
        minWins: body.min_wins_for_ranking,
        topN: body.ranking_top_n,
        gold: body.badge_gold_pct,
        silver: body.badge_silver_pct,
        bronze: body.badge_bronze_pct,
      });
      setSaveFlash(true);
      window.setTimeout(() => setSaveFlash(false), 2200);
      queryClient.invalidateQueries({ queryKey: ['game-rps-config'] });
      queryClient.invalidateQueries({ queryKey: ['game-rps-ranking'] });
    },
    onError: (err) => {
      setFormError(err?.message || t('gameRps.form.saveError'));
    },
  });

  const parsed = useMemo(() => {
    const min = Number(minWins);
    const n = Number(topN);
    const g = Number(goldPct);
    const s = Number(silverPct);
    const b = Number(bronzePct);
    return { min, n, g, s, b };
  }, [minWins, topN, goldPct, silverPct, bronzePct]);

  const pctSumOk =
    Number.isInteger(parsed.g) &&
    Number.isInteger(parsed.s) &&
    Number.isInteger(parsed.b) &&
    parsed.g > 0 &&
    parsed.s > 0 &&
    parsed.b > 0 &&
    parsed.g + parsed.s + parsed.b === 100;

  const minOk =
    Number.isInteger(parsed.min) &&
    parsed.min >= MIN_WINS_FLOOR &&
    parsed.min <= MIN_WINS_CEIL;

  const topNOk =
    Number.isInteger(parsed.n) &&
    parsed.n >= TOP_N_FLOOR &&
    parsed.n <= TOP_N_CEIL;

  const isDirty =
    minOk &&
    topNOk &&
    pctSumOk &&
    (parsed.min !== saved.minWins ||
      parsed.n !== saved.topN ||
      parsed.g !== saved.gold ||
      parsed.s !== saved.silver ||
      parsed.b !== saved.bronze);

  const cuts = pctSumOk && topNOk ? badgeCutoffs(parsed.n, parsed.g, parsed.s) : { goldEnd: 0, silverEnd: 0 };

  const nudge = (delta) => {
    const base = Number.isInteger(parsed.min) ? parsed.min : saved.minWins;
    setMinWins(String(clampWins(base + delta)));
    setFormError('');
  };

  const nudgeTopN = (delta) => {
    const base = Number.isInteger(parsed.n) ? parsed.n : saved.topN;
    setTopN(String(clampTopN(base + delta)));
    setFormError('');
  };

  const onSave = (e) => {
    e.preventDefault();
    if (!minOk) {
      setFormError(t('gameRps.form.invalidMinWins'));
      return;
    }
    if (!topNOk) {
      setFormError(t('gameRps.form.invalidTopN'));
      return;
    }
    if (!pctSumOk) {
      setFormError(t('gameRps.form.invalidPct'));
      return;
    }
    mutation.mutate({
      min_wins_for_ranking: parsed.min,
      ranking_top_n: parsed.n,
      badge_gold_pct: parsed.g,
      badge_silver_pct: parsed.s,
      badge_bronze_pct: parsed.b,
    });
  };

  const ranking = useMemo(() => rankingData?.data?.ranking || [], [rankingData?.data?.ranking]);
  const rankingUserIds = useMemo(() => ranking.map((r) => r.user_id), [ranking]);
  const { usersById } = useUsersByIds(rankingUserIds, token);
  const periodKey = rankingData?.data?.period_key || rankingData?.data?.season_key || '';
  const source = rankingData?.data?.source || '';
  const showTier = ranking.some((r) => r.tier != null);
  const refreshing =
    tab === 'config'
      ? configFetching
      : rankingFetching || seasonsFetching;

  const onRefresh = () => {
    if (tab === 'config') refetchConfig();
    else {
      refetchSeasons();
      refetchRanking();
    }
  };

  const sourceLabel = () => {
    if (source === 'live') return t('gameRps.ranking.live');
    if (source === 'season_badges') return t('gameRps.ranking.seasonBadges');
    return t('gameRps.ranking.snapshot');
  };

  return (
    <div className="px-4 py-6 sm:px-6 max-w-[920px] space-y-5 animate-fade-in">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 mb-1.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary border border-primary/25">
              <Trophy size={18} strokeWidth={2.25} />
            </span>
            <h1 className="text-xl sm:text-2xl font-bold text-dark-text tracking-tight">
              {t('gameRps.title')}
            </h1>
          </div>
          <p className="text-sm text-dark-text-secondary pl-[46px]">
            {t('gameRps.description')}
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dark-border text-sm text-dark-text-secondary hover:border-primary/50 hover:text-primary transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : undefined} />
          {t('gameRps.actions.refresh')}
        </button>
      </header>

      <GameRpsTabs active={tab} />

      {tab === 'config' && (
        <div className="space-y-4">
          {configLoading && (
            <div className="rounded-xl border border-dark-border bg-dark-card/60 p-8 flex items-center justify-center gap-2 text-dark-text-secondary text-sm">
              <Loader2 size={16} className="animate-spin text-primary" />
              {t('gameRps.loading')}
            </div>
          )}
          {configError && (
            <p className="text-sm text-red-400">
              {configErr?.message || t('gameRps.loadError')}
            </p>
          )}
          {!configLoading && !configError && (
            <form
              onSubmit={onSave}
              className="rounded-xl border border-dark-border bg-gradient-to-br from-dark-card to-dark-surface/40 p-5 sm:p-6 shadow-sm space-y-6"
            >
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/90 mb-1">
                  {t('gameRps.form.badge')}
                </p>
                <h2 className="text-lg font-semibold text-dark-text">
                  {t('gameRps.form.minWins')}
                </h2>
                <p className="text-sm text-dark-text-secondary mt-1 max-w-md leading-relaxed">
                  {t('gameRps.form.minWinsHint', {
                    n: Number.isInteger(parsed.n) ? parsed.n : DEFAULT_TOP_N,
                  })}
                </p>
              </div>

              <div className="rounded-xl border border-dark-border/80 bg-dark-bg/50 p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                  <button
                    type="button"
                    aria-label="decrease"
                    onClick={() => nudge(-1)}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-dark-border bg-dark-surface text-dark-text hover:border-primary/45 hover:text-primary transition-colors"
                  >
                    <Minus size={18} />
                  </button>
                  <input
                    type="number"
                    min={MIN_WINS_FLOOR}
                    max={MIN_WINS_CEIL}
                    value={minWins}
                    onChange={(e) => {
                      setMinWins(e.target.value);
                      setFormError('');
                    }}
                    className="w-[7.5rem] sm:w-36 text-center text-3xl sm:text-4xl font-bold tabular-nums rounded-xl border border-dark-border bg-dark-card text-dark-text py-3 px-2 focus:border-primary/60 focus:ring-1 focus:ring-primary/35 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    aria-label="increase"
                    onClick={() => nudge(1)}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-dark-border bg-dark-surface text-dark-text hover:border-primary/45 hover:text-primary transition-colors"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>

              <div>
                <h2 className="text-lg font-semibold text-dark-text">
                  {t('gameRps.form.topN')}
                </h2>
                <p className="text-sm text-dark-text-secondary mt-1 max-w-md leading-relaxed">
                  {t('gameRps.form.topNHint')}
                </p>
              </div>

              <div className="rounded-xl border border-dark-border/80 bg-dark-bg/50 p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                  <button
                    type="button"
                    aria-label="decrease top n"
                    onClick={() => nudgeTopN(-1)}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-dark-border bg-dark-surface text-dark-text hover:border-primary/45 hover:text-primary transition-colors"
                  >
                    <Minus size={18} />
                  </button>
                  <input
                    type="number"
                    min={TOP_N_FLOOR}
                    max={TOP_N_CEIL}
                    value={topN}
                    onChange={(e) => {
                      setTopN(e.target.value);
                      setFormError('');
                    }}
                    className="w-[7.5rem] sm:w-36 text-center text-3xl sm:text-4xl font-bold tabular-nums rounded-xl border border-dark-border bg-dark-card text-dark-text py-3 px-2 focus:border-primary/60 focus:ring-1 focus:ring-primary/35 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    aria-label="increase top n"
                    onClick={() => nudgeTopN(1)}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-dark-border bg-dark-surface text-dark-text hover:border-primary/45 hover:text-primary transition-colors"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <h3 className="text-base font-semibold text-dark-text">
                    {t('gameRps.form.tierTitle', {
                      n: Number.isInteger(parsed.n) ? parsed.n : DEFAULT_TOP_N,
                    })}
                  </h3>
                  <p className="text-sm text-dark-text-secondary mt-0.5">
                    {t('gameRps.form.tierHint')}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <PctField
                    label={t('gameRps.tier.gold')}
                    value={goldPct}
                    onChange={(v) => {
                      setGoldPct(v);
                      setFormError('');
                    }}
                    accent="text-primary"
                  />
                  <PctField
                    label={t('gameRps.tier.silver')}
                    value={silverPct}
                    onChange={(v) => {
                      setSilverPct(v);
                      setFormError('');
                    }}
                    accent="text-zinc-300"
                  />
                  <PctField
                    label={t('gameRps.tier.bronze')}
                    value={bronzePct}
                    onChange={(v) => {
                      setBronzePct(v);
                      setFormError('');
                    }}
                    accent="text-amber-300"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span
                    className={`tabular-nums ${
                      pctSumOk ? 'text-emerald-400' : 'text-red-400'
                    }`}
                  >
                    {t('gameRps.form.pctSum', {
                      sum: Number.isFinite(parsed.g + parsed.s + parsed.b)
                        ? parsed.g + parsed.s + parsed.b
                        : '—',
                    })}
                  </span>
                  {pctSumOk && topNOk && (
                    <span className="text-dark-text-muted">
                      {t('gameRps.form.cutoffPreview', {
                        gold: cuts.goldEnd,
                        silver: cuts.silverEnd,
                        n: parsed.n,
                      })}
                    </span>
                  )}
                </div>
              </div>

              {formError && <p className="text-sm text-red-400">{formError}</p>}
              {saveFlash && !formError && (
                <p className="inline-flex items-center gap-1.5 text-sm text-emerald-400">
                  <Check size={15} />
                  {t('gameRps.form.saved')}
                </p>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                <p className="text-xs text-dark-text-muted">
                  {isDirty ? t('gameRps.form.unsaved') : t('gameRps.form.synced')}
                </p>
                <button
                  type="submit"
                  disabled={mutation.isPending || !isDirty || !minOk || !topNOk || !pctSumOk}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-black text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed min-w-[7.5rem]"
                >
                  {mutation.isPending ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      {t('gameRps.actions.saving')}
                    </>
                  ) : (
                    t('gameRps.actions.save')
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {tab === 'ranking' && (
        <div className="space-y-4">
          <div className="inline-flex p-1 rounded-xl bg-dark-surface/80 border border-dark-border">
            <button
              type="button"
              onClick={() => setRankMode('season')}
              className={[
                'px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                rankMode === 'season'
                  ? 'bg-dark-card text-dark-text border border-dark-border shadow-sm'
                  : 'text-dark-text-secondary hover:text-dark-text border border-transparent',
              ].join(' ')}
            >
              {t('gameRps.ranking.bySeason')}
            </button>
            <button
              type="button"
              onClick={() => setRankMode('period')}
              className={[
                'px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                rankMode === 'period'
                  ? 'bg-dark-card text-dark-text border border-dark-border shadow-sm'
                  : 'text-dark-text-secondary hover:text-dark-text border border-transparent',
              ].join(' ')}
            >
              {t('gameRps.ranking.byPeriod')}
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            {rankMode === 'season' ? (
              <select
                value={seasonKey}
                onChange={(e) => setSeasonKey(e.target.value)}
                className="rounded-lg border border-dark-border bg-dark-card text-dark-text text-sm px-3 py-2 focus:border-primary/60 outline-none min-w-[12rem]"
              >
                {seasons.map((s) => (
                  <option key={s.season_key} value={s.season_key}>
                    {s.season_key}
                    {s.current ? ` (${t('gameRps.ranking.current')})` : ''}
                    {s.closed_at ? '' : s.current ? '' : ''}
                  </option>
                ))}
              </select>
            ) : (
              <div className="inline-flex p-1 rounded-xl bg-dark-surface/80 border border-dark-border">
                {PERIODS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPeriod(p)}
                    className={[
                      'px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                      period === p
                        ? 'bg-dark-card text-dark-text border border-dark-border shadow-sm'
                        : 'text-dark-text-secondary hover:text-dark-text border border-transparent',
                    ].join(' ')}
                  >
                    {t(`gameRps.period.${p}`)}
                  </button>
                ))}
              </div>
            )}

            {(periodKey || source) && (
              <div className="flex items-center gap-2 text-xs text-dark-text-secondary">
                {periodKey && (
                  <span className="font-mono text-dark-text-muted">{periodKey}</span>
                )}
                {source && (
                  <span
                    className={[
                      'inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium',
                      source === 'live'
                        ? 'border-emerald-500/35 bg-emerald-500/10 text-emerald-300'
                        : source === 'season_badges'
                          ? 'border-primary/35 bg-primary/10 text-primary'
                          : 'border-sky-500/35 bg-sky-500/10 text-sky-300',
                    ].join(' ')}
                  >
                    {sourceLabel()}
                  </span>
                )}
              </div>
            )}
          </div>

          {rankingLoading && (
            <div className="rounded-xl border border-dark-border bg-dark-card/60 p-8 flex items-center justify-center gap-2 text-dark-text-secondary text-sm">
              <Loader2 size={16} className="animate-spin text-primary" />
              {t('gameRps.loading')}
            </div>
          )}
          {rankingError && (
            <p className="text-sm text-red-400">
              {rankingErr?.message || t('gameRps.ranking.loadError')}
            </p>
          )}
          {!rankingLoading && !rankingError && ranking.length === 0 && (
            <div className="rounded-xl border border-dashed border-dark-border bg-dark-card/40 px-5 py-10 text-center text-sm text-dark-text-secondary">
              {t('gameRps.ranking.empty')}
            </div>
          )}
          {!rankingLoading && !rankingError && ranking.length > 0 && (
            <div className="rounded-xl border border-dark-border overflow-hidden bg-gradient-to-br from-dark-card to-dark-surface/30">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-dark-border bg-dark-surface/50 text-left text-xs uppercase tracking-wide text-dark-text-muted">
                      <th className="px-4 py-3 font-semibold w-20">
                        {t('gameRps.ranking.rank')}
                      </th>
                      <th className="px-4 py-3 font-semibold">
                        {t('gameRps.ranking.userId')}
                      </th>
                      {showTier && (
                        <th className="px-4 py-3 font-semibold">
                          {t('gameRps.ranking.tier')}
                        </th>
                      )}
                      <th className="px-4 py-3 font-semibold text-right">
                        {t('gameRps.ranking.wins')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranking.map((row) => (
                      <tr
                        key={`${row.user_id}-${row.rank}`}
                        className="border-b border-dark-border last:border-0 hover:bg-dark-surface/40 transition-colors"
                      >
                        <td className="px-4 py-2.5">
                          <RankBadge rank={row.rank} />
                        </td>
                        <td className="px-4 py-2.5">
                          <UserChip
                            userId={row.user_id}
                            user={usersById[row.user_id] || usersById[Number(row.user_id)]}
                          />
                        </td>
                        {showTier && (
                          <td className="px-4 py-2.5">
                            <TierChip tier={row.tier} />
                          </td>
                        )}
                        <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-primary">
                          {row.wins}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
